import { ResponsiveBar } from '@nivo/bar';
import { ResponsivePie } from '@nivo/pie';
import _ from 'lodash';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TwTheme } from '../../Tw';
import {
  applyColorOpacity,
  getBestContrastColor,
} from '../common/color.helper';
import { formatPercent } from '../common/formatHelper';
import {
  HeatmapBucket,
  HeatmapBucketStatField,
  HeatmapBucketStats,
} from '../common/heatmap.helper';
import { HeatmapMetricDescriptor } from '../common/heatmap.types';
import { cn } from '../common/utils';
import ButtonSwitchMulti from '../components/actions/ButtonSwitchMulti';
import * as Icon from '../components/icons';
import FeedStockPanel from '../feed/panels/FeedStockPanel';
import { HeatmapFilter } from '../simulation/store/simulation.types';

const colors = TwTheme.extend.colors;
const AGGREGATION_THRESHOLD = 30;
const TOP_ITEMS_LIMIT = 20;

export type HeatmapLegendChartsProps = {
  metricDescriptor: HeatmapMetricDescriptor<string, Record<string, any>>;
  buckets: HeatmapBucket[];
  field: HeatmapBucketStatField;
  showPercentage: boolean;
  totalStats: Record<HeatmapBucketStatField, number>;
  heatmapFilter: HeatmapFilter;
  chartType?: 'bar' | 'pie' | 'abc';
};

type ChartDataItem = HeatmapBucket & HeatmapBucketStats;

const aggregateSmallValues = (
  items: any[],
  threshold: number = AGGREGATION_THRESHOLD,
  topLimit: number = TOP_ITEMS_LIMIT,
): any[] => {
  if (items.length <= threshold) {
    return items;
  }

  // Sort by value in descending order
  const sortedItems = [...items].sort((a, b) => b.value - a.value);

  // Take top N items
  const topItems = sortedItems.slice(0, topLimit);

  // Calculate the sum of remaining items
  const remainingItems = sortedItems.slice(topLimit);
  const remainingTotal = remainingItems.reduce(
    (sum, item) => sum + item.value,
    0,
  );
  const remainingRawTotal = remainingItems.reduce(
    (sum, item) => sum + item.rawValue,
    0,
  );
  const remainingPercentage = remainingItems.reduce(
    (sum, item) => sum + item.percentage,
    0,
  );

  // Add "Others" category if there are remaining items
  if (remainingItems.length > 0) {
    topItems.push({
      id: 'Others',
      value: remainingTotal,
      color: TwTheme.extend.colors.chart.empty,
      rawValue: remainingRawTotal,
      percentage: remainingPercentage,
      opacity: 1,
    });
  }

  return topItems;
};

const HeatmapLegendCharts: React.FC<HeatmapLegendChartsProps> = props => {
  const { t } = useTranslation('app');
  const [chartType, setChartType] = useState<'bar' | 'pie' | 'abc'>('bar');

  // Use passed chartType prop if available, otherwise use local state
  const activeChartType = props.chartType || chartType;

  const chartItemsCount = props.buckets.length;
  const showTicks = chartItemsCount < 10;
  const rotateTicks = chartItemsCount > 5;

  // Calculate maximum text length and adjust margins accordingly
  const maxTextLength = _.maxBy(props.buckets, b => b.title.length).title
    .length;
  const baseBottomMargin = 30;
  const additionalMarginPerChar = 4; // Increased from 2 to 4 pixels per character
  const minBottomMargin = 40;
  const maxBottomMargin = 200; // Increased from 150 to 200 for very long labels

  const allSelected = !_.some(props.heatmapFilter.hiddenBuckets);

  const bottomMargin = rotateTicks
    ? Math.min(
        Math.max(
          baseBottomMargin + maxTextLength * additionalMarginPerChar,
          minBottomMargin,
        ),
        maxBottomMargin,
      )
    : minBottomMargin;

  // Calculate dynamic height based on text length and rotation
  const baseHeight = 12; // rem - increased base height
  const additionalHeightPerChar = 0.15; // rem per character
  const minHeight = 12; // rem
  const maxHeight = 32; // rem

  const barChartHeight = `${Math.min(
    Math.max(
      baseHeight + (rotateTicks ? maxTextLength * additionalHeightPerChar : 0),
      minHeight,
    ),
    maxHeight,
  )}rem`;

  const pieChartHeight = '20rem'; // Fixed height for pie chart to maintain good proportions

  const chartHeight =
    activeChartType === 'bar'
      ? barChartHeight
      : activeChartType === 'pie'
        ? pieChartHeight
        : null;

  const chartData = _.map(props.buckets, b => ({
    ...b.stats,
    ..._.omit(b, ['stats', 'levelStats']),
  }));

  function getLabel(d: ChartDataItem) {
    const value =
      props.metricDescriptor.rangeType === 'distinct' ? d[props.field] : d.from;
    const total = props.totalStats[props.field];

    return props.showPercentage
      ? formatPercent(value / total)
      : props.metricDescriptor.format
        ? props.metricDescriptor.format(value)
        : value?.toString();
  }

  function getColor(d: ChartDataItem) {
    const isSelected =
      allSelected || props.heatmapFilter.hiddenBuckets[d.id] !== true;
    return isSelected ? d.color : applyColorOpacity(d.color, 0.2);
  }
  return (
    <div className="bg-menu/50 mb-2">
      {!props.chartType && chartData && chartData.length > 0 && (
        <div className="flex items-center justify-end p-1">
          <ButtonSwitchMulti
            // className={cn('border-menu/50 rounded border !p-1')}
            buttonType="minimal"
            options={[
              {
                // iconLeft: true,
                // label: t`Bar`,
                icon: (
                  <div className={cn('flex w-full items-center')}>
                    <Icon.ViewGraph
                      className={cn(
                        'fill-current',
                        'group-hover:opacity-50',
                        'h-4 w-full',
                      )}
                    />
                  </div>
                ),
              },
              {
                // iconRight: true,
                // label: t`Pie`,
                icon: (
                  <div className={cn('flex w-full items-center')}>
                    <Icon.DataStatsPie
                      className={cn(
                        'fill-current',
                        'group-hover:opacity-50',
                        'h-4 w-full',
                      )}
                    />
                  </div>
                ),
              },
              ...(props.metricDescriptor?.type === 'categoryABC' ||
              props.metricDescriptor?.type === 'abc' ||
              props.metricDescriptor?.type === 'percentRank'
                ? [
                    {
                      // label: t`ABC`,
                      icon: (
                        <div className={cn('flex w-full items-center')}>
                          <span
                            className={cn(
                              'text-xs font-bold',
                              'group-hover:opacity-50',
                            )}
                          >
                            ABC
                          </span>
                        </div>
                      ),
                    },
                  ]
                : []),
            ]}
            selectedIndex={
              activeChartType === 'bar'
                ? 0
                : activeChartType === 'pie'
                  ? 1
                  : activeChartType === 'abc'
                    ? 2
                    : 0
            }
            onClick={index => {
              if (index === 0) setChartType('bar');
              else if (index === 1) setChartType('pie');
              else if (index === 2) setChartType('abc');
            }}
            autoSize
          />
        </div>
      )}
      {chartHeight && (
        <div style={{ height: chartHeight }}>
          {activeChartType === 'bar' ? (
            <ResponsiveBar
              data={chartData}
              keys={[props.field]}
              indexBy="title"
              margin={{ top: 10, right: 10, bottom: bottomMargin, left: 10 }}
              padding={0.3}
              colors={({ data }) => getColor(data)}
              borderRadius={2}
              axisBottom={
                showTicks && {
                  tickSize: showTicks ? 5 : 0,
                  tickPadding: 5,
                  tickRotation: rotateTicks ? -90 : 0,
                }
              }
              axisLeft={null}
              enableGridX={false}
              enableGridY={true}
              enableLabel={true}
              label={datum => {
                return datum.formattedValue;
              }}
              labelSkipWidth={32}
              labelSkipHeight={12}
              labelTextColor={({ data }) => {
                const item = data as unknown as ChartDataItem;
                return getBestContrastColor(item.color);
              }}
              theme={{
                axis: {
                  ticks: {
                    text: {
                      fill: colors.chart.ticks,
                      fontSize: 10,
                    },
                  },
                },
                grid: {
                  line: {
                    stroke: colors.chart.grid,
                  },
                },
                labels: {
                  text: {
                    // fill: '#ffffff',
                    fontSize: 11,
                  },
                },
              }}
              tooltip={({ data }) => {
                return (
                  <div
                    className={cn(
                      'z-dropdown_menu flex flex-col',
                      'animate-appear',
                      'bg-app-panel-dark/90 backdrop-blur backdrop-filter',
                      'shadow-xl',
                      'p-2',
                      'rounded',
                    )}
                  >
                    <strong>{data.title}</strong>
                    <div>{getLabel(data)}</div>
                  </div>
                );
              }}
            />
          ) : activeChartType === 'pie' ? (
            <ResponsivePie
              data={chartData}
              value={props.field}
              margin={{ top: 20, right: 20, bottom: 20, left: 20 }}
              innerRadius={0.5}
              padAngle={1}
              cornerRadius={1.5}
              colors={({ data }) => getColor(data)}
              borderWidth={1}
              borderColor={{ from: 'color', modifiers: [['darker', 0.2]] }}
              enableArcLinkLabels={false}
              arcLinkLabelsSkipAngle={10}
              arcLinkLabelsTextColor="rgba(255, 255, 255, 0.7)"
              arcLinkLabelsThickness={2}
              arcLinkLabelsColor={{ from: 'color' }}
              arcLabelsSkipAngle={10}
              arcLabel={({ data }) => getLabel(data)}
              theme={{
                labels: {
                  text: {
                    fontSize: 11,
                    fill: '#ffffff',
                  },
                },
              }}
              tooltip={({ datum: { data } }) => (
                <div
                  className={cn(
                    'z-dropdown_menu flex flex-col',
                    'animate-appear',
                    'bg-app-panel-dark/90 backdrop-blur backdrop-filter',
                    'shadow-xl',
                    'p-2',
                    'rounded',
                  )}
                >
                  <strong>{data.title}</strong>
                  <div>{getLabel(data)}</div>
                </div>
              )}
            />
          ) : null}
        </div>
      )}
      {activeChartType === 'abc' && <FeedStockPanel />}
    </div>
  );
};

export default HeatmapLegendCharts;
